(function () {
  'use strict';

  /* =========================================================================
     PART 1 — Cart integration

     Dimplery's storefront runs GoKwik, which intercepts the Add to Cart
     click itself and makes its own request to Shopify's cart API -- it
     never lets the page's <form> fire a native 'submit' event. That means
     a handler listening for 'submit' (what an earlier version of this file
     did) never runs at all when GoKwik is active, and nothing we do gets
     attached.

     This version uses two independent mechanisms, matched to what each
     engraving combination actually needs:

     1. TEXT and/or SYMBOL only (no uploaded file): we patch window.fetch
        and XMLHttpRequest so that ANY call to /cart/add or /cart/add.js --
        no matter which script made it, GoKwik's included -- gets our
        engraving properties merged into its JSON body before it's sent.
        This is the same technique that fixed the very first version of
        this bug, and it works regardless of who actually triggers the
        add-to-cart call.

     2. An uploaded photo (or anything that needs a real file): a file can
        never travel over that JSON endpoint, full stop -- that's a hard
        Shopify platform limitation, not something we can route around.
        For this case we intercept the Add to Cart click directly (in the
        capture phase, so we run before GoKwik's own click handler can),
        cancel it outright, and submit a real multipart POST to /cart/add
        ourselves, redirecting to /cart afterward.
  ========================================================================= */

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ''));
  }

  function mergeIntoJsonBody(bodyText, propsObj) {
    var data;
    try {
      data = JSON.parse(bodyText);
    } catch (e) {
      return bodyText; // not a JSON body we understand -- leave it alone
    }
    data.properties = Object.assign({}, data.properties || {}, propsObj);
    return JSON.stringify(data);
  }

  // Reads one engrave instance's current state and returns either:
  //  - null                         -- engraving is off, nothing to add
  //  - { needsFile: true }          -- a file is involved; the click-intercept
  //                                    path below owns this submission instead
  //  - { needsFile: false, props }  -- plain text/symbol properties to merge
  function collectEngraveProperties(root) {
    var toggle = root.querySelector('[data-engrave-toggle]');
    if (toggle && !toggle.checked) return null;

    var isChecked = function (sel) {
      var el = root.querySelector(sel);
      return !!(el && el.checked);
    };
    var activeText = isChecked('[data-engrave-addon="text"]');
    var activeSymbol = isChecked('[data-engrave-addon="symbol"]');
    var activeUpload = isChecked('[data-engrave-addon="upload"]');

    if (activeUpload) return { needsFile: true };
    if (!activeText && !activeSymbol) return null;

    var props = {};
    var types = [];

    if (activeText) {
      var textarea = root.querySelector('[data-engrave-textarea]');
      if (textarea && textarea.value.trim().length) {
        types.push('Text');
        props['Engraving Text'] = textarea.value;
        var line2Toggle = root.querySelector('[data-engrave-second-line-toggle]');
        var line2 = root.querySelector('[data-engrave-textarea-line2]');
        if (line2Toggle && line2Toggle.checked && line2 && line2.value.trim().length) {
          props['Engraving Text Line 2'] = line2.value;
        }
        var styleSelect = root.querySelector('[data-engrave-style-select]');
        if (styleSelect) {
          var styleOpt = styleSelect.options[styleSelect.selectedIndex];
          if (styleOpt) props['Font Style'] = styleOpt.value;
        }
      }
    }

    if (activeSymbol) {
      var symbolField = root.querySelector('[data-engrave-symbol-field]');
      if (symbolField && symbolField.value) {
        types.push('Symbol');
        props['Symbol'] = symbolField.value;
      }
    }

    if (!types.length) return null;
    props['Engraving Type'] = types.join(' + ');
    return { needsFile: false, props: props };
  }

  function getPendingEngraveProperties() {
    var roots = document.querySelectorAll('[data-engrave]');
    for (var i = 0; i < roots.length; i++) {
      var result = collectEngraveProperties(roots[i]);
      if (!result) continue;
      if (result.needsFile) return null; // the click-intercept path owns this one
      return result.props;
    }
    return null;
  }

  if (!window.__engraveFetchPatched) {
    window.__engraveFetchPatched = true;
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url);
        if (isCartAddUrl(url) && init && typeof init.body === 'string') {
          var props = getPendingEngraveProperties();
          if (props) {
            init = Object.assign({}, init, { body: mergeIntoJsonBody(init.body, props) });
          }
        }
      } catch (e) {
        // Never let our own logic block a real add-to-cart request.
      }
      return nativeFetch.call(this, input, init);
    };
  }

  if (!window.__engraveXhrPatched) {
    window.__engraveXhrPatched = true;
    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__engraveUrl = url;
      return nativeOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (isCartAddUrl(this.__engraveUrl) && typeof body === 'string') {
          var props = getPendingEngraveProperties();
          if (props) body = mergeIntoJsonBody(body, props);
        }
      } catch (e) {
        // Never let our own logic block a real add-to-cart request.
      }
      return nativeSend.call(this, body);
    };
  }

  function submitEngravedForm(form, root) {
    var iframeName = 'engrave-upload-frame';
    var iframe = document.querySelector('iframe[name="' + iframeName + '"]');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.name = iframeName;
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
    }

    var originalTarget = form.target;
    var originalEnctype = form.enctype;
    var originalMethod = form.method;
    var originalAction = form.action;

    form.target = iframeName;
    form.enctype = 'multipart/form-data';
    form.method = 'post';
    form.action = '/cart/add';
    form.submit();

    // Restore in case the customer stays on the page for any reason.
    setTimeout(function () {
      form.target = originalTarget;
      form.enctype = originalEnctype;
      form.method = originalMethod;
      form.action = originalAction;
    }, 50);

    setTimeout(function () {
      window.location.href = '/cart';
    }, 1200);
  }

  /* =========================================================================
     PART 2 — Per-instance engraving UI
  ========================================================================= */

  function initEngrave(root) {
    var maxChars = parseInt(root.getAttribute('data-max-chars'), 10) || 10;

    var toggle = root.querySelector('[data-engrave-toggle]');
    var body = root.querySelector('[data-engrave-body]');
    var addonInputs = root.querySelectorAll('[data-engrave-addon]');
    var typeField = root.querySelector('[data-engrave-type-field]');

    var textarea = root.querySelector('[data-engrave-textarea]');
    var line2 = root.querySelector('[data-engrave-textarea-line2]');
    var counter = root.querySelector('[data-engrave-counter]');
    var styleSelect = root.querySelector('[data-engrave-style-select]');
    var secondLineToggle = root.querySelector('[data-engrave-second-line-toggle]');

    var symbolSelect = root.querySelector('[data-engrave-symbol-select]');
    var symbolField = root.querySelector('[data-engrave-symbol-field]');

    var uploadTypeInputs = root.querySelectorAll('[data-engrave-upload-type] input');
    var instructionBlocks = root.querySelectorAll('[data-engrave-instructions]');
    var fileInput = root.querySelector('[data-engrave-file-input]');
    var fileLabel = root.querySelector('[data-engrave-file-label]');

    var previewRoot = root.querySelector('[data-engrave-preview]');
    var previewEmpty = root.querySelector('[data-engrave-preview-empty]');
    var previewSymbolBox = root.querySelector('[data-engrave-preview-symbol]');
    var previewTextBox = root.querySelector('[data-engrave-preview-text]');
    var previewUploadBox = root.querySelector('[data-engrave-preview-upload]');
    var previewUploadImg = root.querySelector('[data-engrave-preview-upload-img]');
    var previewUploadLabel = root.querySelector('[data-engrave-preview-upload-label]');

    var generatedPreviewInput = root.querySelector('[data-engrave-generated-preview-input]');

    function activeAddons() {
      var active = {};
      addonInputs.forEach(function (input) {
        if (input.checked) active[input.getAttribute('data-engrave-addon')] = true;
      });
      return active;
    }

    function setSectionVisibility() {
      var active = activeAddons();
      root.querySelectorAll('[data-engrave-section]').forEach(function (section) {
        var name = section.getAttribute('data-engrave-section');
        var isActive = !!active[name];
        section.hidden = !isActive;
        section.querySelectorAll('input, textarea, select').forEach(function (field) {
          field.disabled = !isActive || (toggle && !toggle.checked);
        });
      });
      if (typeField) {
        var labels = Object.keys(active).map(function (k) {
          return k.charAt(0).toUpperCase() + k.slice(1);
        });
        typeField.value = labels.length ? labels.join(' + ') : 'None';
      }
    }

    function setBodyFieldsDisabled(disabled) {
      if (!body) return;
      body.querySelectorAll('input, textarea, select').forEach(function (field) {
        field.disabled = disabled;
      });
      if (!disabled) setSectionVisibility(); // re-apply per-addon disabling on top
    }

    if (toggle && body) {
      toggle.addEventListener('change', function () {
        body.hidden = !toggle.checked;
        setBodyFieldsDisabled(!toggle.checked);
        updatePreview();
      });
      setBodyFieldsDisabled(true);
    }

    addonInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        setSectionVisibility();
        updatePreview();
      });
    });

    /* ---- Text ---- */
    function applyFontFamily() {
      if (!styleSelect) return;
      var selected = styleSelect.options[styleSelect.selectedIndex];
      var fontFamily = selected ? selected.getAttribute('data-font-family') : '';
      var fontStyle = selected ? (selected.getAttribute('data-font-style') || 'normal') : 'normal';
      if (textarea) textarea.style.fontFamily = fontFamily;
      if (line2) line2.style.fontFamily = fontFamily;
      if (previewTextBox) {
        previewTextBox.style.fontFamily = fontFamily;
        previewTextBox.style.fontStyle = fontStyle;
      }
    }

    if (textarea && counter) {
      textarea.addEventListener('input', function () {
        var len = textarea.value.length;
        counter.textContent = len + ' / ' + maxChars;
        counter.classList.toggle('is-limit', len >= maxChars);
        updatePreview();
      });
    }

    if (styleSelect) {
      styleSelect.addEventListener('change', function () {
        applyFontFamily();
        updatePreview();
      });
      applyFontFamily();
    }

    if (secondLineToggle && line2) {
      secondLineToggle.addEventListener('change', function () {
        line2.hidden = !secondLineToggle.checked;
        if (!secondLineToggle.checked) line2.value = '';
        updatePreview();
      });
      line2.addEventListener('input', updatePreview);
    }

    /* ---- Symbol (native select — only ever loads the ONE selected icon) ---- */
    function currentSymbolOption() {
      return symbolSelect ? symbolSelect.options[symbolSelect.selectedIndex] : null;
    }

    if (symbolSelect) {
      symbolSelect.addEventListener('change', function () {
        var opt = currentSymbolOption();
        if (symbolField && opt) symbolField.value = opt.value;
        updateSymbolTrigger();
        updatePreview();
      });
      var initialOpt = currentSymbolOption();
      if (symbolField && initialOpt) symbolField.value = initialOpt.value;
    }

    /* ---- Custom symbol picker (shows an icon preview per option) ----
       The native <select> above stays the real source of truth (and the
       accessible fallback); this just builds a nicer picker on top of it.
       Category icon grids are built ONLY the first time that category tab
       is opened -- never all at once -- so opening this never fires more
       than one category's worth of image requests (10-20 icons), instead
       of all ~130 at once like the old broken version did. */
    var symbolPicker = root.querySelector('[data-engrave-symbol-picker]');
    var symbolTrigger = root.querySelector('[data-engrave-symbol-trigger]');
    var symbolTriggerIcon = root.querySelector('[data-engrave-symbol-trigger-icon]');
    var symbolTriggerLabel = root.querySelector('[data-engrave-symbol-trigger-label]');
    var symbolPanel = root.querySelector('[data-engrave-symbol-panel]');
    var symbolTabsEl = root.querySelector('[data-engrave-symbol-tabs]');
    var symbolGridEl = root.querySelector('[data-engrave-symbol-grid]');

    function updateSymbolTrigger() {
      if (!symbolTrigger) return;
      var opt = currentSymbolOption();
      if (!opt) return;
      var svgSrc = opt.getAttribute('data-svg-src');
      symbolTriggerIcon.innerHTML = svgSrc ? '<img src="' + svgSrc + '" alt="" loading="lazy">' : '';
      symbolTriggerLabel.textContent = opt.textContent;
    }

    if (symbolPicker && symbolSelect && symbolTabsEl && symbolGridEl) {
      var groups = symbolSelect.querySelectorAll('optgroup');
      var builtCategories = {}; // avoids rebuilding a category's grid on repeat opens

      function renderCategory(index) {
        var group = groups[index];
        if (!group) return;

        Array.prototype.forEach.call(symbolTabsEl.children, function (tab, i) {
          tab.classList.toggle('is-active', i === index);
        });

        if (!builtCategories[index]) {
          var grid = document.createElement('div');
          grid.className = 'engrave__symbol-category-grid';
          Array.prototype.forEach.call(group.querySelectorAll('option'), function (opt) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'engrave__symbol-btn';
            var svgSrc = opt.getAttribute('data-svg-src');
            btn.innerHTML =
              '<span class="engrave__symbol-btn-icon">' + (svgSrc ? '<img src="' + svgSrc + '" alt="" loading="lazy">' : '') + '</span>' +
              '<span class="engrave__symbol-btn-name">' + opt.textContent + '</span>';
            btn.addEventListener('click', function () {
              symbolSelect.value = opt.value;
              symbolSelect.dispatchEvent(new Event('change', { bubbles: true }));
              closeSymbolPanel();
            });
            grid.appendChild(btn);
          });
          builtCategories[index] = grid;
        }

        symbolGridEl.innerHTML = '';
        symbolGridEl.appendChild(builtCategories[index]);
      }

      Array.prototype.forEach.call(groups, function (group, index) {
        var tab = document.createElement('button');
        tab.type = 'button';
        tab.className = 'engrave__symbol-tab';
        tab.textContent = group.getAttribute('label');
        tab.addEventListener('click', function () { renderCategory(index); });
        symbolTabsEl.appendChild(tab);
      });

      function openSymbolPanel() {
        symbolPanel.hidden = false;
        symbolTrigger.setAttribute('aria-expanded', 'true');
        if (!Object.keys(builtCategories).length) renderCategory(0);
      }
      function closeSymbolPanel() {
        symbolPanel.hidden = true;
        symbolTrigger.setAttribute('aria-expanded', 'false');
      }

      symbolTrigger.addEventListener('click', function () {
        if (symbolPanel.hidden) openSymbolPanel(); else closeSymbolPanel();
      });
      document.addEventListener('click', function (e) {
        if (!symbolPicker.contains(e.target)) closeSymbolPanel();
      });

      updateSymbolTrigger();
    }

    /* ---- Upload ---- */
    uploadTypeInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        instructionBlocks.forEach(function (block) {
          block.hidden = block.getAttribute('data-engrave-instructions') !== input.value;
        });
      });
    });

    var currentUploadDataUrl = null;

    if (fileInput) {
      fileInput.addEventListener('change', function () {
        var file = fileInput.files && fileInput.files[0];
        if (fileLabel) fileLabel.textContent = file ? file.name : 'Choose a photo';
        if (!file) {
          currentUploadDataUrl = null;
          updatePreview();
          return;
        }
        var reader = new FileReader();
        reader.onload = function (e) {
          currentUploadDataUrl = e.target.result;
          updatePreview();
        };
        reader.readAsDataURL(file);
      });
    }

    /* ---- Unified shared preview ---- */
    function updatePreview() {
      if (!previewRoot) return;
      var active = activeAddons();
      var engagedOn = !toggle || toggle.checked;
      var anythingToShow = false;

      // Text
      if (engagedOn && active.text && textarea && textarea.value.trim().length) {
        var text = textarea.value;
        if (secondLineToggle && secondLineToggle.checked && line2 && line2.value.trim().length) {
          text += '\n' + line2.value;
        }
        previewTextBox.textContent = text;
        previewTextBox.hidden = false;
        anythingToShow = true;
      } else if (previewTextBox) {
        previewTextBox.hidden = true;
      }

      // Symbol
      if (engagedOn && active.symbol && symbolSelect) {
        var opt = currentSymbolOption();
        if (opt) {
          var svgSrc = opt.getAttribute('data-svg-src');
          previewSymbolBox.innerHTML = svgSrc ? '<img src="' + svgSrc + '" alt="' + opt.value + '">' : '';
          previewSymbolBox.hidden = false;
          anythingToShow = true;
        }
      } else if (previewSymbolBox) {
        previewSymbolBox.hidden = true;
      }

      // Upload
      if (engagedOn && active.upload && currentUploadDataUrl) {
        previewUploadImg.src = currentUploadDataUrl;
        var typeInput = root.querySelector('[data-engrave-upload-type] input:checked');
        previewUploadLabel.textContent = typeInput ? typeInput.value : 'Upload';
        previewUploadBox.hidden = false;
        anythingToShow = true;
      } else if (previewUploadBox) {
        previewUploadBox.hidden = true;
      }

      if (previewEmpty) previewEmpty.hidden = anythingToShow;
    }

    updatePreview();

    /* =======================================================================
       PART 3 — Generate a flattened composite PNG of the active configuration
       and attach it as a real file via the hidden generatedPreviewInput, so
       the engraving team gets one image instead of raw property text.
    ======================================================================= */

    function loadImageFromUrl(url) {
      return new Promise(function (resolve, reject) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () { resolve(img); };
        img.onerror = reject;
        img.src = url;
      });
    }

    function buildCompositePreviewBlob() {
      return (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(function () {
        var active = activeAddons();
        var canvas = document.createElement('canvas');
        canvas.width = 900;
        canvas.height = 500;
        var ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FBF7EF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        var drawn = [];

        if (active.symbol && symbolSelect) {
          var opt = currentSymbolOption();
          var svgSrc = opt ? opt.getAttribute('data-svg-src') : null;
          if (svgSrc) {
            drawn.push(loadImageFromUrl(svgSrc).then(function (img) {
              var size = 160;
              ctx.drawImage(img, canvas.width / 2 - size / 2, 40, size, size);
            }).catch(function () {}));
          }
        }

        if (active.upload && currentUploadDataUrl) {
          drawn.push(loadImageFromUrl(currentUploadDataUrl).then(function (img) {
            var maxW = 260, maxH = 260;
            var ratio = Math.min(maxW / img.width, maxH / img.height, 1);
            var w = img.width * ratio, h = img.height * ratio;
            ctx.drawImage(img, canvas.width / 2 - w / 2, 210, w, h);
          }).catch(function () {}));
        }

        return Promise.all(drawn).then(function () {
          if (active.text && textarea && textarea.value.trim().length) {
            var selected = styleSelect ? styleSelect.options[styleSelect.selectedIndex] : null;
            var fontFamily = selected ? selected.getAttribute('data-font-family') : 'inherit';
            ctx.fillStyle = '#584738';
            ctx.textAlign = 'center';
            ctx.font = '48px ' + fontFamily;
            var yBase = 420;
            ctx.fillText(textarea.value, canvas.width / 2, yBase);
            if (secondLineToggle && secondLineToggle.checked && line2 && line2.value.trim().length) {
              ctx.fillText(line2.value, canvas.width / 2, yBase + 55);
            }
          }

          ctx.fillStyle = '#8A6E4B';
          ctx.font = '16px "Helvetica Neue", Arial, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText('Dimplery engraving preview — for reference only', 20, canvas.height - 16);

          return new Promise(function (resolve) {
            canvas.toBlob(function (blob) { resolve(blob); }, 'image/png');
          });
        });
      });
    }

    function attachGeneratedPreview() {
      if (!generatedPreviewInput || !window.DataTransfer) return Promise.resolve();
      return buildCompositePreviewBlob().then(function (blob) {
        if (!blob) return;
        var file = new File([blob], 'engraving-preview.png', { type: 'image/png' });
        var dt = new DataTransfer();
        dt.items.add(file);
        generatedPreviewInput.files = dt.files;
      }).catch(function () {
        // If canvas generation fails for any reason, submission still proceeds
        // with the raw text/symbol/upload properties — nothing is blocked on this.
      });
    }

    /* =======================================================================
       PART 4 — Optional paid-engraving variant swap
    ======================================================================= */

    var standardVariantId = root.getAttribute('data-standard-variant-id');
    var engravedVariantId = root.getAttribute('data-engraved-variant-id');

    function swapVariant(useEngraved) {
      if (!standardVariantId || !engravedVariantId) return;
      var formId = root.getAttribute('data-form-id');
      var form = formId ? document.getElementById(formId) : root.closest('form');
      if (!form) return;
      var variantInput = form.querySelector('input[name="id"], select[name="id"]');
      if (!variantInput) return;
      variantInput.value = useEngraved ? engravedVariantId : standardVariantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (toggle && standardVariantId && engravedVariantId) {
      toggle.addEventListener('change', function () {
        swapVariant(toggle.checked);
      });
    }

    /* =======================================================================
       PART 5 — Submission

       Every engraved order -- text, symbol, upload, or any combination --
       gets a generated flattened preview image attached (so the engraving
       team always has one picture to work from, not just raw property
       text), which means every engraved order needs the real multipart
       path, not just uploads. GoKwik intercepts the Add to Cart CLICK
       directly rather than letting a 'submit' event fire, so we intercept
       at the same level -- the click -- in the capture phase, so we run
       before GoKwik's own handler gets a chance to.

       The fetch/XHR patch in Part 1 stays in place as a safety net: if this
       click-intercept ever fails to win the race against GoKwik for some
       reason, plain text/symbol properties still attach via that path --
       just without the generated image for that one order.
    ======================================================================= */

    var formId = root.getAttribute('data-form-id');
    var form = formId ? document.getElementById(formId) : root.closest('form');

    if (form) {
      document.addEventListener('click', function (e) {
        if (!toggle || !toggle.checked) return; // engraving off -- leave this click alone

        var control = e.target.closest('button, input[type="submit"], input[type="button"]');
        if (!control) return;
        // control.form reflects true form ownership (ancestry OR the form="..."
        // attribute), regardless of where the button actually sits in the DOM.
        if (control.form !== form) return;

        var active = activeAddons();
        if (!Object.keys(active).length) return; // nothing selected -- nothing to intercept

        if (active.text && (!textarea || textarea.value.trim().length === 0)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (textarea) { textarea.focus(); textarea.classList.add('has-error'); }
          alert('Please enter your engraving text, or uncheck the Text add-on.');
          return;
        }
        if (active.symbol && symbolField && !symbolField.value) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Please choose a symbol.');
          return;
        }
        if (active.upload && (!fileInput || !fileInput.files || fileInput.files.length === 0)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          alert('Please upload a photo, or uncheck the upload add-on.');
          return;
        }

        // Cancel the click entirely -- this stops GoKwik's own handler from
        // ever running -- and submit our own multipart POST instead, with
        // the generated preview image attached.
        e.preventDefault();
        e.stopImmediatePropagation();

        attachGeneratedPreview().then(function () {
          submitEngravedForm(form, root);
        });
      }, true); // capture phase: document-level capture listeners run before
                // listeners attached directly to the button, whether GoKwik's
                // own handler is capture or bubble.
    }
  }

  document.querySelectorAll('[data-engrave]').forEach(initEngrave);
})();
